import { useState } from 'react'
import { api, useAbruf, type ApiFehler } from '../api'
import { Angabe, Fehleranzeige, Karte, Merkmal, Tabelle, Zustand } from '../bausteine'
import { datum, geld } from '../formate'

interface Postenzeile {
  id: string
  belegnummer: string
  art: string
  status: string
  kundeId: string
  belegdatum: string
  faelligAm: string
  betrag: string
  ausgeglichen: string
  rueckstand: string
  verzugAb: string | null
  mahnstufe: string
  letzteMahnungAm: string | null
}

interface Mahnungszeile {
  id: string
  stufe: string
  mahndatum: string
  zahlbarBis: string
  hauptforderung: string
  mahngebuehr: string
  verzugszinsen: string
  gesamtbetrag: string
  sperrandrohung: boolean
  posten: number
}

interface Laufergebnis {
  stichtag: string
  anzahl: number
  mahnungen: Mahnungszeile[]
}

interface Sperrblatt {
  zulaessig: boolean
  verzugsbetrag: string
  hindernisse: string[]
  ankuendigungWirksamAb: string
}

export default function Debitoren() {
  const heute = new Date().toISOString().slice(0, 10)
  const ueberfaellig = useAbruf<Postenzeile[]>('/api/debitoren/ueberfaellig')

  const [stichtag, setzeStichtag] = useState(heute)
  const [ergebnis, setzeErgebnis] = useState<Laufergebnis>()
  const [laeuft, setzeLaeuft] = useState(false)
  const [fehler, setzeFehler] = useState<ApiFehler>()

  const [kundennummer, setzeKundennummer] = useState('')
  const [sperre, setzeSperre] = useState<Sperrblatt>()
  const [sperrfehler, setzeSperrfehler] = useState<ApiFehler>()

  async function mahnlauf() {
    setzeFehler(undefined)
    setzeLaeuft(true)
    try {
      setzeErgebnis(await api.sende<Laufergebnis>('/api/mahnwesen/laeufe', { stichtag }))
      ueberfaellig.erneut()
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler)
    } finally {
      setzeLaeuft(false)
    }
  }

  async function sperrpruefung() {
    setzeSperrfehler(undefined)
    setzeSperre(undefined)
    try {
      setzeSperre(
        await api.lies<Sperrblatt>(
          `/api/mahnwesen/sperrpruefung/nach-kundennummer/${kundennummer}`,
        ),
      )
    } catch (ursache) {
      setzeSperrfehler(ursache as ApiFehler)
    }
  }

  return (
    <>
      <h1>Debitoren und Mahnwesen</h1>
      <p className="untertitel">
        Beanstandete und gestundete Posten bleiben vom Mahnlauf ausgenommen und zählen
        nach Paragraf 41f EnWG nicht zum Verzugsbetrag.
      </p>

      <Karte titel="Mahnlauf">
        {fehler && <Fehleranzeige fehler={fehler} />}
        <div className="leiste" style={{ marginBottom: ergebnis ? 16 : 0 }}>
          <div className="feld">
            <label htmlFor="stichtag">Stichtag</label>
            <input
              id="stichtag"
              type="date"
              value={stichtag}
              onChange={(ereignis) => setzeStichtag(ereignis.target.value)}
            />
          </div>
          <button className="betont" disabled={laeuft} onClick={mahnlauf}>
            {laeuft ? 'Läuft …' : 'Mahnlauf durchführen'}
          </button>
        </div>
        {ergebnis && (
          <>
            <div className="hinweis info">
              {ergebnis.anzahl === 0
                ? 'Der Lauf hat keine Mahnung erzeugt.'
                : `${ergebnis.anzahl} Mahnungen erzeugt.`}
            </div>
            {ergebnis.mahnungen.length > 0 && (
              <Tabelle
                spalten={[
                  { name: 'Stufe' },
                  { name: 'Mahndatum' },
                  { name: 'Zahlbar bis' },
                  { name: 'Posten', zahl: true },
                  { name: 'Hauptforderung', zahl: true },
                  { name: 'Gebühr', zahl: true },
                  { name: 'Zinsen', zahl: true },
                  { name: 'Gesamt', zahl: true },
                ]}
              >
                {ergebnis.mahnungen.map((mahnung) => (
                  <tr key={mahnung.id}>
                    <td>
                      <Merkmal art={mahnung.sperrandrohung ? 'fehler' : 'warnung'}>
                        {mahnung.stufe}
                      </Merkmal>
                    </td>
                    <td>{datum(mahnung.mahndatum)}</td>
                    <td>{datum(mahnung.zahlbarBis)}</td>
                    <td className="zahl">{mahnung.posten}</td>
                    <td className="zahl">{geld(mahnung.hauptforderung)}</td>
                    <td className="zahl">{geld(mahnung.mahngebuehr)}</td>
                    <td className="zahl">{geld(mahnung.verzugszinsen)}</td>
                    <td className="zahl">{geld(mahnung.gesamtbetrag)}</td>
                  </tr>
                ))}
              </Tabelle>
            )}
          </>
        )}
      </Karte>

      <Karte titel="Sperrprüfung nach Paragraf 41f EnWG">
        {sperrfehler && <Fehleranzeige fehler={sperrfehler} />}
        <div className="leiste" style={{ marginBottom: sperre ? 16 : 0 }}>
          <div className="feld">
            <label htmlFor="kunde">Kundennummer</label>
            <input
              id="kunde"
              style={{ width: 200 }}
              placeholder="1000000001"
              value={kundennummer}
              onChange={(ereignis) => setzeKundennummer(ereignis.target.value)}
            />
          </div>
          <button disabled={!kundennummer} onClick={sperrpruefung}>
            Prüfen
          </button>
        </div>
        {sperre && (
          <>
            <div className={`hinweis ${sperre.zulaessig ? 'warnung' : 'info'}`}>
              {sperre.zulaessig
                ? 'Eine Versorgungsunterbrechung wäre zulässig. Die Entscheidung trifft ein Mensch.'
                : 'Eine Versorgungsunterbrechung ist nicht zulässig.'}
              {sperre.hindernisse.length > 0 && (
                <ul>
                  {sperre.hindernisse.map((hindernis) => (
                    <li key={hindernis}>{hindernis}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="angaben">
              <Angabe beschriftung="Verzugsbetrag">{geld(sperre.verzugsbetrag)}</Angabe>
              <Angabe beschriftung="Ankündigung wirksam ab">
                {datum(sperre.ankuendigungWirksamAb)}
              </Angabe>
            </div>
          </>
        )}
      </Karte>

      <Karte titel="Überfällige Posten">
        <Zustand
          laeuft={ueberfaellig.laeuft}
          fehler={ueberfaellig.fehler}
          leer={(ueberfaellig.daten ?? []).length === 0}
          leertext="Keine überfälligen Posten."
        >
          <Tabelle
            spalten={[
              { name: 'Belegnummer' },
              { name: 'Art' },
              { name: 'Belegdatum' },
              { name: 'Fällig am' },
              { name: 'Verzug ab' },
              { name: 'Mahnstufe' },
              { name: 'Rückstand', zahl: true },
            ]}
          >
            {(ueberfaellig.daten ?? []).map((posten) => (
              <tr key={posten.id}>
                <td>{posten.belegnummer}</td>
                <td>{posten.art}</td>
                <td>{datum(posten.belegdatum)}</td>
                <td>{datum(posten.faelligAm)}</td>
                <td>{datum(posten.verzugAb)}</td>
                <td>
                  <Merkmal art={posten.mahnstufe === 'KEINE' ? 'neutral' : 'warnung'}>
                    {posten.mahnstufe}
                  </Merkmal>
                </td>
                <td className="zahl">{geld(posten.rueckstand)}</td>
              </tr>
            ))}
          </Tabelle>
        </Zustand>
      </Karte>
    </>
  )
}
